import { Bell, CalendarCheck, CheckCircle, ChevronDown, ChevronUp, DollarSign } from "lucide-react";
import { useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import { colors, radii, spacing, typographyScale } from "../../../styles/design-system";
import CustomSwitch from "../../common/general/CustomSwitch/CustomSwitch";
import CustomText from "../../common/general/CustomText/CustomText";

export type AccountNotificationPreferenceKey = "enrollmentDeadlines" | "financial";

export type AccountNotificationPreferenceItem = {
  key: AccountNotificationPreferenceKey;
  title: string;
  description: string;
  enabled: boolean;
};

type AccountNotificationPreferencesProps = {
  items: AccountNotificationPreferenceItem[];
  initialExpandedItemKey?: AccountNotificationPreferenceKey | null;
  onToggleItem: (key: AccountNotificationPreferenceKey, enabled: boolean) => void;
};

const notificationDetailsByKey: Record<
  AccountNotificationPreferenceKey,
  {
    notifications: string[];
    footer?: string;
  }
> = {
  enrollmentDeadlines: {
    notifications: [
      "Registration opens",
      "Add/drop period ending",
      "Last day to drop with W",
      "Graduation application due",
    ],
  },
  financial: {
    notifications: [
      "FAFSA deadlines & reminders",
      "State financial aid deadline",
      "Payment plan reminders",
      "Payment installment due",
    ],
    footer: "Additional options like VA benefits or employer reimbursement will appear if applicable to your account.",
  },
};

const getItemIcon = (key: AccountNotificationPreferenceKey) => {
  switch (key) {
    case "enrollmentDeadlines":
      return <CalendarCheck size={16} color={colors.warning[600]} aria-hidden={true} />;
    case "financial":
      return <DollarSign size={16} color={colors.success[600]} aria-hidden={true} />;
  }
};

const getAvatarStyle = (key: AccountNotificationPreferenceKey) => {
  switch (key) {
    case "enrollmentDeadlines":
      return [styles.itemAvatar, styles.enrollmentAvatar];
    case "financial":
      return [styles.itemAvatar, styles.financialAvatar];
  }
};

export const AccountNotificationPreferences = ({
  items,
  initialExpandedItemKey = null,
  onToggleItem,
}: AccountNotificationPreferencesProps) => {
  const [expandedItemKey, setExpandedItemKey] = useState<AccountNotificationPreferenceKey | null>(
    initialExpandedItemKey
  );

  return (
    <View style={styles.sectionContainer}>
      <View style={styles.sectionHeaderContent}>
        <View style={styles.sectionTitleGroup}>
          <Bell size={18} color={colors.grey[500]} aria-hidden={true} />
          <CustomText text="Account notifications" useTranslationText={false} style={styles.sectionHeaderTitle} />
        </View>
        <CustomText
          text="Registration, deadlines, and financial alerts - these apply to your whole account, not a single course."
          useTranslationText={false}
          style={styles.sectionSubtitle}
        />
      </View>

      <View style={styles.itemList}>
        {items.map((item) => {
          const isExpanded = expandedItemKey === item.key;

          return (
            <View key={item.key}>
              <Pressable
                role="button"
                accessibilityState={{ expanded: isExpanded }}
                accessibilityLabel={`${item.title} account notification details ${isExpanded ? "collapse" : "expand"}`}
                onPress={() => setExpandedItemKey((previousKey) => (previousKey === item.key ? null : item.key))}
                style={({ hovered }) => [styles.itemRow, hovered && styles.itemRowHover]}>
                <View style={getAvatarStyle(item.key)}>{getItemIcon(item.key)}</View>
                <View style={styles.itemText}>
                  <CustomText text={item.title} useTranslationText={false} style={styles.itemTitle} />
                  <CustomText text={item.description} useTranslationText={false} style={styles.itemDescription} />
                </View>
                <View style={styles.itemControls}>
                  <CustomSwitch
                    value={item.enabled}
                    onPress={(event) => {
                      event.stopPropagation();
                    }}
                    onValueChange={(enabled) => onToggleItem(item.key, enabled)}
                    accessibilityLabel={`${item.title} account notifications`}
                  />
                  <View style={styles.expandIcon}>
                    {isExpanded ? (
                      <ChevronUp size={18} color={colors.grey[500]} aria-hidden={true} />
                    ) : (
                      <ChevronDown size={18} color={colors.grey[500]} aria-hidden={true} />
                    )}
                  </View>
                </View>
              </Pressable>

              {isExpanded ? (
                <View style={styles.detailPanel}>
                  <CustomText
                    text="When enabled, you'll receive:"
                    useTranslationText={false}
                    style={styles.detailIntro}
                  />
                  <View style={styles.detailList}>
                    {notificationDetailsByKey[item.key].notifications.map((notification) => (
                      <View key={notification} style={styles.detailListItem}>
                        <CheckCircle size={13} color={colors.success[600]} aria-hidden={true} />
                        <CustomText text={notification} useTranslationText={false} style={styles.detailItemText} />
                      </View>
                    ))}
                  </View>
                  {notificationDetailsByKey[item.key].footer ? (
                    <View style={styles.detailFooter}>
                      <CustomText
                        text={notificationDetailsByKey[item.key].footer ?? ""}
                        useTranslationText={false}
                        style={styles.detailFooterText}
                      />
                    </View>
                  ) : null}
                </View>
              ) : null}
            </View>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  sectionContainer: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.lg,
    overflow: "hidden",
  },
  sectionHeaderContent: {
    paddingTop: spacing.lg,
    paddingBottom: spacing.sm,
    paddingHorizontal: spacing.lg,
  },
  sectionTitleGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  sectionHeaderTitle: {
    color: colors.grey[800],
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
    fontWeight: "600",
  },
  sectionSubtitle: {
    color: colors.grey[500],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    marginTop: spacing.xxs,
  },
  itemList: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  itemRow: {
    minHeight: 72,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  itemRowHover: {
    backgroundColor: colors.grey[25],
  },
  itemAvatar: {
    width: spacing.xl,
    height: spacing.xl,
    borderRadius: radii.full,
    alignItems: "center",
    justifyContent: "center",
  },
  enrollmentAvatar: {
    backgroundColor: colors.warning[100],
  },
  financialAvatar: {
    backgroundColor: colors.success[100],
  },
  itemText: {
    flex: 1,
    gap: spacing.xxs,
  },
  itemTitle: {
    color: colors.grey[800],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
  },
  itemDescription: {
    color: colors.grey[500],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
  },
  itemControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    marginLeft: spacing.sm,
  },
  expandIcon: {
    width: spacing.lg,
    height: spacing.lg,
    alignItems: "center",
    justifyContent: "center",
  },
  detailPanel: {
    paddingTop: spacing.sm,
    paddingBottom: spacing.lg,
    paddingHorizontal: spacing.lg,
    backgroundColor: colors.grey[50],
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  detailIntro: {
    color: colors.grey[600],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
  },
  detailList: {
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  detailListItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  detailItemText: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  detailFooter: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  detailFooterText: {
    color: colors.grey[600],
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
  },
});
